import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  HardDrive, 
  Users, 
  Shield, 
  Activity,
  FolderOpen,
  Share2,
  Lock,
  BarChart3,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Calendar,
  CreditCard,
  Settings
} from 'lucide-react';

type ViewType = 'overview' | 'management' | 'activity' | 'workspace';

const VIEWS: ViewType[] = ['overview', 'management', 'activity', 'workspace'];

interface ServiceData {
  name: string;
  status: 'active' | 'pending' | 'expired';
  storage: string;
  users: number;
  expires: string;
}

interface ActivityItem {
  action: string;
  time: string;
  icon: string;
  user?: string;
  ip?: string;
  type: 'management' | 'login';
}

export function DashboardShowcase() {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState<ViewType>('overview');
  const [autoRotate, setAutoRotate] = useState(true);

  // Sample data - realistic service names and values
  const services: ServiceData[] = [
    { name: t('showcase.workspace.mainOfficeStorage'), status: 'active', storage: '2.4 TB / 5 TB', users: 12, expires: '45' },
    { name: 'Development Server', status: 'active', storage: '850 GB / 2 TB', users: 8, expires: '22' },
    { name: 'Backup Archive', status: 'pending', storage: '4.8 TB / 5 TB', users: 2, expires: '3' },
  ];

  const activities: ActivityItem[] = [
    { action: `${t('showcase.activity.actions.createdShare')} "projects"`, time: '2 min', icon: '➕', user: 'admin', ip: '192.168.1.100', type: 'management' },
    { action: t('showcase.activity.actions.userLogin'), time: '5 min', icon: '🔑', user: 'sarah', ip: '10.8.0.45', type: 'login' },
    { action: t('showcase.activity.actions.updatedPermissions'), time: '15 min', icon: '✏️', user: 'admin', ip: '192.168.1.100', type: 'management' },
    { action: t('showcase.activity.actions.userLogin'), time: '23 min', icon: '🔑', user: 'mike', ip: '10.8.0.78', type: 'login' },
    { action: `${t('showcase.activity.actions.addedToGroup')} "developers"`, time: '45 min', icon: '➕', user: 'admin', ip: '192.168.1.100', type: 'management' },
    { action: t('showcase.activity.actions.userLogin'), time: '1h', icon: '🔑', user: 'john', ip: '10.8.0.123', type: 'login' },
    { action: t('showcase.activity.actions.modifiedNFS'), time: '2h', icon: '✏️', user: 'admin', ip: '192.168.1.100', type: 'management' },
    { action: `${t('showcase.activity.actions.deletedUser')} "temp"`, time: '3h', icon: '🗑️', user: 'admin', ip: '192.168.1.100', type: 'management' },
  ];

  // Auto-rotate through views
  useEffect(() => {
    if (!autoRotate) return;
    
    const interval = setInterval(() => {
      setCurrentView(prev => {
        const currentIndex = VIEWS.indexOf(prev);
        const nextIndex = (currentIndex + 1) % VIEWS.length;
        return VIEWS[nextIndex];
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [autoRotate]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-500/20';
      case 'pending': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-500/20';
      case 'expired': return 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-500/20';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-500/20';
    }
  };

  const renderOverview = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-semibold text-foreground">{t('showcase.overview.title')}</div>
        <div className="flex items-center space-x-2">
          <Activity className="w-4 h-4 text-green-500" />
          <span className="text-sm text-muted-foreground">{t('showcase.overview.status')}</span>
        </div>
      </div>

      <div className="grid gap-3">
        {services.map((service, idx) => (
          <div 
            key={idx} 
            className="p-4 rounded-xl bg-gradient-to-br from-card to-card/50 border border-border/50 hover:border-primary/30 transition-all duration-300"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-medium text-foreground mb-1">{service.name}</div>
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span className="flex items-center">
                    <HardDrive className="w-3 h-3 mr-1" />
                    {service.storage}
                  </span>
                  <span className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    {service.users} users
                  </span>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(service.status)}`}>
                {service.status}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {t('showcase.overview.expires')} {service.expires} {t('common.days')}
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="w-3 h-3 text-purple-500" />
                <FolderOpen className="w-3 h-3 text-blue-500" />
                <Share2 className="w-3 h-3 text-green-500" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderManagement = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-semibold text-foreground">{t('showcase.management.title')}</div>
        <Settings className="w-5 h-5 text-muted-foreground" />
      </div>

      {/* Samba Share Management Preview */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-card to-card/50 border border-border/50">
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium text-foreground flex items-center">
            <FolderOpen className="w-5 h-5 text-blue-500 mr-2" />
            {t('showcase.management.sambaShares')}
          </div>
          <button className="text-xs px-3 py-1 bg-primary text-primary-foreground rounded-md font-medium">
            {t('showcase.management.createNewShare')}
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
            <span className="text-sm">projects</span>
            <span className="text-xs text-muted-foreground">dev, admin</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
            <span className="text-sm">documents</span>
            <span className="text-xs text-muted-foreground">@office</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
            <span className="text-sm">backup</span>
            <span className="text-xs text-muted-foreground">admin</span>
          </div>
        </div>
      </div>

      {/* VPN Users Preview */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-card to-card/50 border border-border/50">
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium text-foreground flex items-center">
            <Shield className="w-5 h-5 text-purple-500 mr-2" />
            {t('showcase.management.vpnUsers')}
          </div>
          <span className="text-xs text-green-500">5 {t('showcase.management.connected')}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
            <span>john</span>
            <span className="text-green-500">●</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
            <span>sarah</span>
            <span className="text-green-500">●</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
            <span>mike</span>
            <span className="text-gray-400">●</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
            <span>lisa</span>
            <span className="text-green-500">●</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActivity = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-semibold text-foreground">{t('showcase.activity.title')}</div>
        <div className="flex items-center space-x-4">
          <select className="text-xs px-2 py-1 border rounded">
            <option>{t('showcase.activity.all')}</option>
            <option>{t('showcase.activity.management')}</option>
            <option>{t('showcase.activity.login')}</option>
          </select>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-muted-foreground">{t('showcase.activity.live')}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {activities.slice(0, 5).map((activity, idx) => (
          <div 
            key={idx}
            className="p-3 rounded-lg bg-gradient-to-br from-card to-card/50 border border-border/50 hover:border-primary/30 transition-all duration-300 animate-fadeInUp"
            style={{ animationDelay: `${idx * 100}ms` }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-base flex-shrink-0 w-5 text-center">{activity.icon}</span>
                <div className="flex-1">
                  <p className="text-sm text-foreground">{activity.action}</p>
                  <p className="text-xs text-muted-foreground">
                    {activity.user} • {activity.time}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">{activity.ip}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
          <p className="text-2xl font-bold text-blue-500">156</p>
          <p className="text-xs text-muted-foreground">{t('showcase.activity.managementActions')}</p>
        </div>
        <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
          <p className="text-2xl font-bold text-green-500">89</p>
          <p className="text-xs text-muted-foreground">{t('showcase.activity.userLogins')}</p>
        </div>
      </div>
    </div>
  );

  const renderWorkspace = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-semibold text-foreground">{t('showcase.workspace.title')}</div>
        <HardDrive className="w-5 h-5 text-muted-foreground" />
      </div>

      <div className="space-y-4">
        {/* Service Info */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-card to-card/50 border border-border/50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-medium text-foreground">{t('showcase.workspace.mainOfficeStorage')}</div>
              <p className="text-xs text-muted-foreground">{t('showcase.workspace.serviceId')}: 28 • IP: 91.225.106.79</p>
            </div>
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400 rounded">{t('showcase.workspace.running')}</span>
          </div>
          
          {/* Usage Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t('showcase.workspace.storageUsed')}</p>
              <p className="text-sm font-medium">0.9 / 50.0 GB</p>
              <div className="w-full bg-muted/50 rounded-full h-1.5 mt-1">
                <div className="h-full bg-primary rounded-full" style={{ width: '1.8%' }} />
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t('showcase.workspace.filesUsed')}</p>
              <p className="text-sm font-medium">40K / 1966K</p>
              <div className="w-full bg-muted/50 rounded-full h-1.5 mt-1">
                <div className="h-full bg-primary rounded-full" style={{ width: '2%' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Service Features */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
            <div className="flex items-center justify-between mb-1">
              <Shield className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-blue-500">Active</span>
            </div>
            <p className="text-sm font-medium text-foreground">{t('showcase.workspace.vpnUsers')}</p>
            <p className="text-xs text-muted-foreground">2 {t('showcase.workspace.connected')}</p>
          </div>
          <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
            <div className="flex items-center justify-between mb-1">
              <FolderOpen className="w-4 h-4 text-green-500" />
              <span className="text-xs text-green-500">3 {t('showcase.workspace.shares')}</span>
            </div>
            <p className="text-sm font-medium text-foreground">{t('showcase.workspace.sambaShares')}</p>
            <p className="text-xs text-muted-foreground">LDAP users</p>
          </div>
          <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20">
            <div className="flex items-center justify-between mb-1">
              <Share2 className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-purple-500">1 {t('showcase.workspace.mount')}</span>
            </div>
            <p className="text-sm font-medium text-foreground">{t('showcase.workspace.nfsMounts')}</p>
            <p className="text-xs text-muted-foreground">/data/nfs/testing</p>
          </div>
          <div className="p-3 rounded-lg bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20">
            <div className="flex items-center justify-between mb-1">
              <Users className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-orange-500">5 {t('showcase.workspace.total')}</span>
            </div>
            <p className="text-sm font-medium text-foreground">{t('showcase.workspace.systemUsers')}</p>
            <p className="text-xs text-muted-foreground">LDAP integrated</p>
          </div>
        </div>

        <div className="flex gap-2">
          <button className="flex-1 py-2 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors">
            {t('showcase.workspace.manageService')}
          </button>
          <button className="flex-1 py-2 px-4 bg-muted text-foreground text-sm font-medium rounded-lg hover:bg-muted/80 transition-colors">
            {t('showcase.workspace.extendService')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'overview': return renderOverview();
      case 'management': return renderManagement();
      case 'activity': return renderActivity();
      case 'workspace': return renderWorkspace();
      default: return renderOverview();
    }
  };

  return (
    <div className="relative w-full max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto">
      <div 
        className="bg-card/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-border/50 overflow-hidden"
        onMouseEnter={() => setAutoRotate(false)}
        onMouseLeave={() => setAutoRotate(true)}
      >
        {/* Window Controls */}
        <div className="bg-muted/50 px-4 py-2 flex items-center justify-between border-b border-border/50">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <div className="flex items-center space-x-1 text-xs text-foreground">
            <span>{t('showcase.dashboard')}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-background/50 border-b border-border/50">
          <div className="flex">
            {VIEWS.map(view => (
              <button
                key={view}
                onClick={() => setCurrentView(view)}
                className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                  currentView === view
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(`showcase.tabs.${view}`)}
                {currentView === view && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8 min-h-[450px] lg:min-h-[500px] max-h-[450px] lg:max-h-[500px] overflow-y-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}